import { useMemo, useState, useCallback } from 'react';

import {
    BadgeIndianRupee,
    ShoppingBag,
    Box,
    Receipt,
    CreditCard,
    Banknote,
    CheckCircle,
    XCircle,
    TrendingUp,
    PieChart,
    Truck,
    Layers,
    BarChart3,
    MapPin,
    Users,
    UserPlus,
    Building2,
    Store,
    Trophy,
    AlertTriangle,
    Sparkles
} from 'lucide-react';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart as RechartsPie,
    Pie,
    Cell,
    BarChart,
    Bar,
    ComposedChart,
} from 'recharts';


import KPICard from '../components/common/KPICard';
import ChartCard from '../components/common/ChartCard';
import DataTable from '../components/common/DataTable';
import InsightCard from '../components/common/InsightCard';
import LeaderboardList from '../components/common/LeaderboardList';
import BestSellersByArea from '../components/common/BestSellersByArea';
import { useFilters } from '../context/FilterContext';
import { deliveries } from '../utils/mockData/deliveries';
import { categories } from '../utils/mockData/categories';
import { stores } from '../utils/mockData/stores';
import { applyMultiLevelFilters } from '../utils/filters';
import { formatCurrency, formatNumber, formatPercentage, formatDate } from '../utils/formatters';
import { aggregateByTimePeriod, groupBy } from '../utils/calculations';
import {
    getTopCitiesByGMV,
    getTopStoresByGMV,
    getTopPincodesByGMV,
    getTopProductsInScope,
    getGrowthComparedToPreviousPeriod,
    getPreviousPeriodOrders,
    getWorstPerformersByGrowth,
    getScopeLabel,
} from '../utils/insights';
import styles from './DashboardOverview.module.css';

function DashboardOverview({ onNavigateToTab }) {
    const { appliedFilters, orders, filterLevel, setCities, setStores, setPincodes, applyFilters, getCityName } = useFilters();
    const [categoryMetric, setCategoryMetric] = useState('revenue');

    // Filter data based on applied filters
    const filteredOrders = useMemo(() => {
        const filters = {
            startDate: appliedFilters.dateRange?.startDate
                ? appliedFilters.dateRange.startDate.toISOString().split('T')[0]
                : null,
            endDate: appliedFilters.dateRange?.endDate
                ? appliedFilters.dateRange.endDate.toISOString().split('T')[0]
                : null,
            cities: appliedFilters.cities,
            stores: appliedFilters.stores,
            pincodes: appliedFilters.pincodes,
        };
        return applyMultiLevelFilters(orders, filters);
    }, [appliedFilters]);

    const filteredDeliveries = useMemo(() => {
        const filters = {
            cities: appliedFilters.cities,
            stores: appliedFilters.stores,
            pincodes: appliedFilters.pincodes,
        };
        return applyMultiLevelFilters(deliveries, filters);
    }, [appliedFilters]);

    // Previous period orders for growth calculations
    const previousPeriodOrders = useMemo(() => {
        if (!appliedFilters.dateRange?.startDate || !appliedFilters.dateRange?.endDate) {
            return [];
        }
        const prevOrders = getPreviousPeriodOrders(
            orders,
            appliedFilters.dateRange.startDate,
            appliedFilters.dateRange.endDate
        );
        // Apply same location filters to previous period
        const filters = {
            cities: appliedFilters.cities,
            stores: appliedFilters.stores,
            pincodes: appliedFilters.pincodes,
        };
        return applyMultiLevelFilters(prevOrders, filters);
    }, [appliedFilters]);

    // Calculate KPI metrics
    const metrics = useMemo(() => {
        const gmv = filteredOrders.reduce((sum, o) => sum + o.total, 0);
        const totalOrders = filteredOrders.length;
        const volume = filteredOrders.reduce((sum, o) => sum + o.itemCount, 0);
        const avgOrderValue = totalOrders > 0 ? gmv / totalOrders : 0;
        const onlineOrders = filteredOrders.filter(o => o.paymentMethod === 'Online').length;
        const codOrders = filteredOrders.filter(o => o.paymentMethod === 'COD').length;

        const delivered = filteredDeliveries.filter(d => d.status === 'Delivered');
        const withinSlot = delivered.filter(d => d.isWithinSlot).length;
        const failed = filteredDeliveries.filter(d => d.status === 'Failed');
        // Customer rejected: customer was unavailable or refused delivery
        const customerRejected = failed.filter(d =>
            d.failureReason === 'Customer Unavailable' || d.failureReason === 'Refused Delivery'
        ).length;
        // Store rejected: operational issues (wrong address, area not serviceable)
        const storeRejected = failed.filter(d =>
            d.failureReason === 'Wrong Address' || d.failureReason === 'Area Not Serviceable'
        ).length;

        // Volume in Kg by category
        let chickenVolumeKg = 0;
        let muttonVolumeKg = 0;
        filteredOrders.forEach(o => {
            (o.items || []).forEach(item => {
                const kg = item.unitQuantity > 0
                    ? item.unitQuantity * item.quantity
                    : item.quantity * 0.5;
                if (item.categoryId === 'CAT-RAW') chickenVolumeKg += kg;
                else if (item.categoryId === 'CAT-MUTTON') muttonVolumeKg += kg;
            });
        });

        // New Customers: unique customers whose isNewCustomer flag is true
        const newCustomerIds = new Set(
            filteredOrders.filter(o => o.isNewCustomer).map(o => o.customerId)
        );
        const newCustomers = newCustomerIds.size;
        const customerOrders = groupBy(filteredOrders, 'customerId');
        const repeatCustomers = Object.values(customerOrders).filter(orders =>
            orders.some(o => !o.isNewCustomer)
        ).length;

        return {
            gmv,
            totalOrders,
            volume,
            avgOrderValue,
            onlineOrders,
            codOrders,
            withinSlot,
            customerRejected,
            storeRejected,
            chickenVolumeKg: Math.round(chickenVolumeKg * 10) / 10,
            muttonVolumeKg: Math.round(muttonVolumeKg * 10) / 10,
            newCustomers,
            repeatCustomers,
            outsideSlot: delivered.length - withinSlot,
        };
    }, [filteredOrders, filteredDeliveries]);

    // ========== INSIGHT DATA ==========

    // Top performers
    const topCities = useMemo(() => getTopCitiesByGMV(filteredOrders, 5), [filteredOrders]);
    const topStores = useMemo(() => getTopStoresByGMV(filteredOrders, 5), [filteredOrders]);
    const topPincodes = useMemo(() => getTopPincodesByGMV(filteredOrders, 5), [filteredOrders]);

    // Growth compared to previous period
    const growthMetrics = useMemo(() =>
        getGrowthComparedToPreviousPeriod(filteredOrders, previousPeriodOrders),
        [filteredOrders, previousPeriodOrders]
    );

    // Worst performers
    const worstCities = useMemo(() =>
        getWorstPerformersByGrowth(filteredOrders, previousPeriodOrders, 'cityId', 5),
        [filteredOrders, previousPeriodOrders]
    );
    const worstPincodes = useMemo(() =>
        getWorstPerformersByGrowth(filteredOrders, previousPeriodOrders, 'pincode', 5),
        [filteredOrders, previousPeriodOrders]
    );

    // Best sellers - use top pincode scope or current scope
    const topPincodeForBestSellers = topPincodes.length > 0 ? topPincodes[0].code : null;
    const bestSellersScope = useMemo(() => {
        if (topPincodeForBestSellers) {
            return filteredOrders.filter(o => o.pincode === topPincodeForBestSellers);
        }
        return filteredOrders;
    }, [filteredOrders, topPincodeForBestSellers]);

    const topProductsByValue = useMemo(() =>
        getTopProductsInScope(bestSellersScope, 5, 'value'),
        [bestSellersScope]
    );
    const topProductsByVolume = useMemo(() =>
        getTopProductsInScope(bestSellersScope, 5, 'volume'),
        [bestSellersScope]
    );

    const scopeLabel = useMemo(() => getScopeLabel(appliedFilters, orders), [appliedFilters, orders]);
    const bestSellersLabel = topPincodeForBestSellers
        ? `${topPincodes[0]?.name || topPincodeForBestSellers} (Top Area)`
        : scopeLabel;

    // Navigation handlers
    const handleViewCityOrders = useCallback(async () => {
        if (topCities.length > 0) {
            setCities([topCities[0].id]);
            await applyFilters();
            if (onNavigateToTab) onNavigateToTab('orders');
        }
    }, [topCities, setCities, applyFilters, onNavigateToTab]);

    const handleViewStoreOrders = useCallback(async () => {
        if (topStores.length > 0) {
            setStores([topStores[0].id]);
            await applyFilters();
            if (onNavigateToTab) onNavigateToTab('orders');
        }
    }, [topStores, setStores, applyFilters, onNavigateToTab]);

    const handleViewPincodeOrders = useCallback(async () => {
        if (topPincodes.length > 0) {
            setPincodes([topPincodes[0].code]);
            await applyFilters();
            if (onNavigateToTab) onNavigateToTab('orders');
        }
    }, [topPincodes, setPincodes, applyFilters, onNavigateToTab]);

    const handleOpenSalesMix = useCallback(() => {
        if (onNavigateToTab) onNavigateToTab('sales-mix');
    }, [onNavigateToTab]);

    // Sparkline data
    const gmvSparkline = useMemo(() => {
        const daily = aggregateByTimePeriod(filteredOrders, 'day');
        return daily.slice(-14).map(d => ({ value: d.total }));
    }, [filteredOrders]);

    // Trend data for line charts
    const trendData = useMemo(() => {
        const daily = aggregateByTimePeriod(filteredOrders, 'day');
        return daily.slice(-30).map(d => ({
            date: formatDate(d.period, 'short'),
            Revenue: Math.round(d.total),
            Orders: d.count,
        }));
    }, [filteredOrders]);

    // Payment split donut
    const paymentData = [
        { name: 'Online', value: metrics.onlineOrders, color: '#50C878' },
        { name: 'COD', value: metrics.codOrders, color: '#FFB347' },
    ];

    // Customer mix donut
    const customerData = [
        { name: 'New Customers', value: metrics.newCustomers, color: '#4A90E2' },
        { name: 'Repeat', value: metrics.repeatCustomers, color: '#7B68EE' },
    ];

    // Delivery health stacked bar
    const deliveryHealthData = useMemo(() => {
        const daily = aggregateByTimePeriod(filteredDeliveries, 'day');
        return daily.slice(-14).map(d => {
            const delivered = d.items.filter(del => del.status === 'Delivered');
            return {
                date: d.period.slice(-5),
                'Within Slot': delivered.filter(del => del.isWithinSlot).length,
                'Outside Slot': delivered.filter(del => !del.isWithinSlot).length,
                'Rejected': d.items.filter(del => del.status === 'Failed').length,
            };
        });
    }, [filteredDeliveries]);

    // Category data
    const EXCLUDED_CATS = new Set(['CAT-RTC-NV', 'CAT-RTC-V', 'CAT-OIL', 'CAT-MSL', 'CAT-OTH']);
    const categoryData = useMemo(() => {
        const catMap = {};
        categories
            .filter(cat => !EXCLUDED_CATS.has(cat.id))
            .forEach(cat => {
                const displayName = cat.id === 'CAT-RAW' ? 'Chicken' : cat.shortName;
                catMap[cat.id] = { name: displayName, color: cat.color, revenue: 0, volume: 0 };
            });

        filteredOrders.forEach(order => {
            order.items.forEach(item => {
                if (catMap[item.categoryId]) {
                    catMap[item.categoryId].revenue += item.total;
                    catMap[item.categoryId].volume += item.quantity;
                }
            });
        });

        return Object.values(catMap).sort((a, b) => b.revenue - a.revenue);
    }, [filteredOrders]);

    // Performance table data
    const performanceData = useMemo(() => {
        const isStoreLevel = filterLevel === 'store' || appliedFilters.stores?.length > 0;
        const isPincodeLevel = appliedFilters.pincodes?.length > 0;

        if (isPincodeLevel) {
            return [];
        }

        if (isStoreLevel || appliedFilters.cities?.length > 0) {
            const storeGroups = groupBy(filteredOrders, 'storeId');
            const storeDeliveryGroups = groupBy(filteredDeliveries, 'storeId');

            return Object.entries(storeGroups).map(([storeId, storeOrders]) => {
                const store = stores.find(s => s.id === storeId);
                const storeDeliveries = storeDeliveryGroups[storeId] || [];
                const delivered = storeDeliveries.filter(d => d.status === 'Delivered');
                const onTime = delivered.filter(d => d.isWithinSlot);
                const rejected = storeDeliveries.filter(d => d.status === 'Failed');

                return {
                    id: storeId,
                    name: store?.name || storeId,
                    gmv: storeOrders.reduce((sum, o) => sum + o.total, 0),
                    orders: storeOrders.length,
                    volume: storeOrders.reduce((sum, o) => sum + o.itemCount, 0),
                    onTimePercent: delivered.length > 0 ? (onTime.length / delivered.length) * 100 : 0,
                    rejectedPercent: storeDeliveries.length > 0 ? (rejected.length / storeDeliveries.length) * 100 : 0,
                };
            }).sort((a, b) => b.gmv - a.gmv);
        } else {
            const cityGroups = groupBy(filteredOrders, 'cityId');
            const cityDeliveryGroups = groupBy(filteredDeliveries, 'cityId');

            return Object.entries(cityGroups).map(([cityId, cityOrders]) => {
                const cityName = getCityName(cityId);
                const cityDeliveries = cityDeliveryGroups[cityId] || [];
                const delivered = cityDeliveries.filter(d => d.status === 'Delivered');
                const onTime = delivered.filter(d => d.isWithinSlot);
                const rejected = cityDeliveries.filter(d => d.status === 'Failed');

                return {
                    id: cityId,
                    name: cityName || cityId,
                    gmv: cityOrders.reduce((sum, o) => sum + o.total, 0),
                    orders: cityOrders.length,
                    volume: cityOrders.reduce((sum, o) => sum + o.itemCount, 0),
                    onTimePercent: delivered.length > 0 ? (onTime.length / delivered.length) * 100 : 0,
                    rejectedPercent: cityDeliveries.length > 0 ? (rejected.length / cityDeliveries.length) * 100 : 0,
                };
            }).sort((a, b) => b.gmv - a.gmv);
        }
    }, [filteredOrders, filteredDeliveries, filterLevel, appliedFilters]);

    const tableColumns = [
        { key: 'name', label: appliedFilters.pincodes?.length > 0 ? 'Pincode' : (appliedFilters.stores?.length > 0 || appliedFilters.cities?.length > 0 ? 'Store' : 'City') },
        { key: 'gmv', label: 'Revenue', render: (v) => formatCurrency(v, { compact: true }) },
        { key: 'orders', label: 'Orders' },
        { key: 'volume', label: 'Volume' },
        { key: 'onTimePercent', label: 'On-Time %', render: (v) => formatPercentage(v) },
        { key: 'rejectedPercent', label: 'Rejected %', render: (v) => formatPercentage(v) },
    ];

    return (
        <div className={styles.overviewPage}>
            {/* Page Header */}
            <div className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>Dashboard Overview</h1>
                <p className={styles.pageSubtitle}>Executive summary of your business performance</p>
            </div>



            {/* KPI Cards */}
            <section className={styles.kpiSection}>
                <div className={styles.kpiGrid}>
                    <KPICard
                        title="Revenue"
                        value={formatCurrency(metrics.gmv, { compact: true })}
                        trend={growthMetrics.gmvGrowth}
                        icon={BadgeIndianRupee}
                        color="success"
                        sparklineData={gmvSparkline}
                        onClick={() => onNavigateToTab?.('orders')}
                    />
                    <KPICard
                        title="Total Orders"
                        value={formatNumber(metrics.totalOrders)}
                        trend={growthMetrics.ordersGrowth}
                        icon={ShoppingBag}
                        color="primary"
                        onClick={() => onNavigateToTab?.('orders')}
                    />
                    <KPICard
                        title="Chicken Vol (Kg)"
                        value={formatNumber(metrics.chickenVolumeKg)}
                        trend={growthMetrics.volumeGrowth}
                        icon={Box}
                        color="secondary"
                        onClick={() => onNavigateToTab?.('sales-mix')}
                    />
                    <KPICard
                        title="Mutton Vol (Kg)"
                        value={formatNumber(metrics.muttonVolumeKg)}
                        trend={growthMetrics.volumeGrowth}
                        icon={Box}
                        color="secondary"
                        onClick={() => onNavigateToTab?.('sales-mix')}
                    />
                    <KPICard
                        title="Avg Order Value"
                        value={formatCurrency(metrics.avgOrderValue)}
                        trend={3.7}
                        icon={Receipt}
                        color="info"
                        onClick={() => onNavigateToTab?.('orders')}
                    />
                    <KPICard
                        title="Online Pay"
                        value={formatNumber(metrics.onlineOrders)}
                        trend={15.8}
                        icon={CreditCard}
                        color="success"
                        onClick={() => onNavigateToTab?.('orders')}
                    />
                    <KPICard
                        title="COD Pay"
                        value={formatNumber(metrics.codOrders)}
                        trend={-2.4}
                        icon={Banknote}
                        color="warning"
                        onClick={() => onNavigateToTab?.('orders')}
                    />
                    <KPICard
                        title="New Customers"
                        value={formatNumber(metrics.newCustomers)}
                        trend={growthMetrics.ordersGrowth}
                        icon={UserPlus}
                        color="primary"
                        onClick={() => onNavigateToTab?.('customers')}
                    />
                    <KPICard
                        title="On-time Delivery"
                        value={formatNumber(metrics.withinSlot)}
                        trend={18.2}
                        icon={CheckCircle}
                        color="success"
                        onClick={() => onNavigateToTab?.('delivery')}
                    />
                    <KPICard
                        title="Customer Rejected"
                        value={formatNumber(metrics.customerRejected)}
                        trend={-8.3}
                        icon={XCircle}
                        color="danger"
                        onClick={() => onNavigateToTab?.('delivery')}
                    />
                    <KPICard
                        title="Store Rejected"
                        value={formatNumber(metrics.storeRejected)}
                        trend={-15.1}
                        icon={AlertTriangle}
                        color="warning"
                        onClick={() => onNavigateToTab?.('delivery')}
                    />
                </div>
            </section>

            {/* ========== TOP PERFORMERS & LOCAL BEST SELLERS ========== */}
            <section className={styles.insightsSection}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                        <Sparkles size={20} />
                        Top Performers & Local Best Sellers
                    </h2>
                    <p className={styles.sectionSubtitle}>
                        Identify your highest-performing locations and products
                    </p>
                </div>

                {/* Top Performer Insight Cards */}
                <div className={styles.insightCardsGrid}>
                    {topCities.length > 0 && (
                        <InsightCard
                            title="Top City"
                            icon={Building2}
                            primaryLabel="#1 by Revenue"
                            primaryValue={topCities[0].name}
                            secondaryStats={[
                                { label: 'Revenue', value: formatCurrency(topCities[0].gmv, { compact: true }) },
                                { label: 'Orders', value: formatNumber(topCities[0].orders) },
                                { label: 'Volume', value: formatNumber(topCities[0].volume) },
                            ]}
                            trendPercent={growthMetrics.gmvGrowth}
                            onViewDetails={handleViewCityOrders}
                            viewDetailsLabel="View Orders"
                            color="primary"
                        />
                    )}

                    {topStores.length > 0 && (
                        <InsightCard
                            title="Top Store"
                            icon={Store}
                            primaryLabel="#1 by Revenue"
                            primaryValue={topStores[0].name}
                            secondaryStats={[
                                { label: 'Revenue', value: formatCurrency(topStores[0].gmv, { compact: true }) },
                                { label: 'Orders', value: formatNumber(topStores[0].orders) },
                                { label: 'Volume', value: formatNumber(topStores[0].volume) },
                            ]}
                            trendPercent={growthMetrics.gmvGrowth}
                            onViewDetails={handleViewStoreOrders}
                            viewDetailsLabel="View Orders"
                            color="success"
                        />
                    )}

                    {topPincodes.length > 0 && (
                        <InsightCard
                            title="Top Area"
                            icon={MapPin}
                            primaryLabel="#1 by Revenue"
                            primaryValue={`${topPincodes[0].code} - ${topPincodes[0].name}`}
                            secondaryStats={[
                                { label: 'Revenue', value: formatCurrency(topPincodes[0].gmv, { compact: true }) },
                                { label: 'Orders', value: formatNumber(topPincodes[0].orders) },
                                { label: 'Volume', value: formatNumber(topPincodes[0].volume) },
                            ]}
                            trendPercent={growthMetrics.gmvGrowth}
                            onViewDetails={handleViewPincodeOrders}
                            viewDetailsLabel="View Orders"
                            color="secondary"
                        />
                    )}
                </div>

                {/* Best Sellers in Top Area */}
                {(topProductsByValue.length > 0 || topProductsByVolume.length > 0) && (
                    <div className={styles.bestSellersWrapper}>
                        <BestSellersByArea
                            title="Best Sellers"
                            scopeLabel={bestSellersLabel}
                            topByValueRows={topProductsByValue}
                            topByVolumeRows={topProductsByVolume}
                            onNavigateSalesMix={handleOpenSalesMix}
                        />
                    </div>
                )}

                {/* Leaderboards */}
                <div className={styles.leaderboardsGrid}>
                    <LeaderboardList
                        title="Top 5 Cities by Revenue"
                        icon={Trophy}
                        rows={topCities}
                        showOrders={true}
                        variant="default"
                    />
                    <LeaderboardList
                        title="Top 5 Pincodes by Revenue"
                        icon={Trophy}
                        rows={topPincodes.map(p => ({ ...p, id: p.code, name: `${p.code} - ${p.name}` }))}
                        showOrders={true}
                        variant="default"
                    />
                </div>

                {/* Attention Needed */}
                {(worstCities.length > 0 || worstPincodes.length > 0) && (
                    <div className={styles.attentionSection}>
                        <h3 className={styles.attentionTitle}>
                            <AlertTriangle size={18} />
                            Attention Needed
                        </h3>
                        <p className={styles.attentionSubtitle}>
                            Areas with declining performance compared to previous period
                        </p>
                        <div className={styles.leaderboardsGrid}>
                            <LeaderboardList
                                title="Cities by Revenue Growth"
                                icon={AlertTriangle}
                                rows={worstCities.map(c => ({
                                    id: c.id,
                                    name: c.name,
                                    gmv: c.currentGMV,
                                    orders: c.orders,
                                    growth: c.growth,
                                }))}
                                showOrders={true}
                                variant="warning"
                            />
                            <LeaderboardList
                                title="Pincodes by Revenue Growth"
                                icon={AlertTriangle}
                                rows={worstPincodes.map(p => ({
                                    id: p.id,
                                    name: p.name,
                                    gmv: p.currentGMV,
                                    orders: p.orders,
                                    growth: p.growth,
                                }))}
                                showOrders={true}
                                variant="warning"
                            />
                        </div>
                    </div>
                )}
            </section>

            {/* Trend Charts */}
            <section className={styles.section}>
                <div className={styles.chartsRow}>
                    <ChartCard title="Revenue Trend (30 Days)" icon={TrendingUp} height={280}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trendData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}K`} />
                                <Tooltip formatter={(v) => formatCurrency(v)} />
                                <Legend />
                                <Line type="monotone" dataKey="Revenue" stroke="#4A90E2" strokeWidth={2} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Orders Trend (30 Days)" icon={ShoppingBag} height={280}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trendData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                                <YAxis tick={{ fontSize: 10 }} />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="Orders" stroke="#50C878" strokeWidth={2} dot={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
            </section>

            {/* Category Snapshot */}
            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                        <Layers size={20} />
                        Category Snapshot
                    </h2>
                    <div className={styles.toggleContainer}>
                        <button className={`${styles.toggleBtn} ${categoryMetric === 'revenue' ? styles.active : ''}`} onClick={() => setCategoryMetric('revenue')}>
                            Revenue
                        </button>
                        <button className={`${styles.toggleBtn} ${categoryMetric === 'volume' ? styles.active : ''}`} onClick={() => setCategoryMetric('volume')}>
                            Volume
                        </button>
                    </div>
                </div>
                <ChartCard title={`${categoryMetric === 'revenue' ? 'Revenue' : 'Volume'} by Category`} icon={BarChart3} height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={categoryData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                            <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => categoryMetric === 'revenue' ? `₹${(v / 1000).toFixed(0)}K` : v} />
                            <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={70} />
                            <Tooltip formatter={(v) => categoryMetric === 'revenue' ? formatCurrency(v) : formatNumber(v)} />
                            <Legend />
                            <Bar dataKey={categoryMetric} radius={[0, 4, 4, 0]}>
                                {categoryData.map((entry, index) => (
                                    <Cell key={index} fill={entry.color} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </section>

            {/* Performance Table */}
            <section className={styles.section}>
                {appliedFilters.pincodes?.length > 0 ? (
                    <div className={styles.scopedMessage}>
                        <MapPin size={24} />
                        <h3>Scoped to Pincode Level</h3>
                        <p>Performance breakdown is available at City and Store levels.</p>
                    </div>
                ) : (
                    <DataTable
                        data={performanceData}
                        columns={tableColumns}
                        title={`${appliedFilters.stores?.length > 0 || appliedFilters.cities?.length > 0 ? 'Store' : 'City'} Performance Summary`}
                        icon={MapPin}
                        exportFilename="performance_summary"
                        defaultPageSize={10}
                    />
                )}
            </section>
        </div>
    );
}

export default DashboardOverview;
